import type { Node as PMNode, NodeType } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import { fg } from '@atlaskit/platform-feature-flags/fg';

/**
 * CCI-20129: A `replaceNode` whose target is a `tableRow` sends an ADF value whose
 * root content is the row itself. A `tableRow` is not valid under `doc`, so by the
 * time the replacement reaches the editor it has been wrapped in a synthetic `table`
 * that carries nothing but schema defaults. The resolved range spans the whole
 * existing table, so `replaceRange` rebuilds the table from that wrapper and the real
 * table's attrs are silently overwritten — `localId` becomes empty and `layout` falls
 * back to `default`. Every later tool call in the session that targets the parent
 * table then fails to resolve it and reports `local_id_not_found`.
 *
 * The same happens on the restore leg of finalize, where the mount-time slice starts
 * *inside* the table and PM synthesises a fresh wrapper around the restored rows.
 *
 * Rather than patch each slice shape, repair the node after the fact: for every attr
 * the rebuilt table left at its schema default, re-apply the value the table carried
 * before the replacement. A genuine whole-table replacement states the values it
 * wants — its own `localId`, `layout`, … — and those are not defaults, so they win.
 */
export function preserveTableAttrsAcrossReplace({
	tr,
	docBeforeReplace,
	startPos,
}: {
	docBeforeReplace: PMNode;
	startPos: number;
	tr: Transaction;
}): Transaction {
	const tableType = tr.doc.type.schema.nodes.table;
	if (!tableType) {
		return tr;
	}

	// Cheapest discriminator first: the overwhelming majority of replacements are not
	// near a table at all, and those exit here having resolved a single position.
	const before = findTable(docBeforeReplace, startPos, tableType);
	if (!before) {
		return tr;
	}

	const after = findTable(tr.doc, startPos, tableType);
	if (!after) {
		return tr;
	}

	const attrs = { ...after.node.attrs };
	let inheritedAny = false;
	for (const name of Object.keys(attrs)) {
		const schemaDefault = tableType.spec.attrs?.[name]?.default;
		if (attrs[name] === schemaDefault && before.node.attrs[name] !== attrs[name]) {
			attrs[name] = before.node.attrs[name];
			inheritedAny = true;
		}
	}

	if (!inheritedAny || !fg('platform_editor_ai_preserve_wrapped_table_attrs')) {
		return tr;
	}

	try {
		return tr.setNodeMarkup(after.pos, undefined, attrs, after.node.marks);
	} catch {
		// Keep the replacement when the node can no longer be addressed.
		return tr;
	}
}

/**
 * The table a replacement at `startPos` lands on: the one the position sits inside,
 * or — when the range was pulled back to the top-level boundary in front of it — the
 * one that starts there.
 */
function findTable(
	doc: PMNode,
	startPos: number,
	tableType: NodeType,
): { node: PMNode; pos: number } | undefined {
	if (startPos < 0 || startPos > doc.content.size) {
		return undefined;
	}

	let $start;
	try {
		$start = doc.resolve(startPos);
	} catch {
		return undefined;
	}

	for (let depth = $start.depth; depth > 0; depth--) {
		const ancestor = $start.node(depth);
		if (ancestor.type === tableType) {
			return { node: ancestor, pos: $start.before(depth) };
		}
	}

	return $start.nodeAfter?.type === tableType
		? { node: $start.nodeAfter, pos: startPos }
		: undefined;
}
